import React, { useMemo, useState, useCallback, useEffect, useRef  } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAllControls } from './redux/selectors/assessmentSelectors';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  Button,
  Snackbar,
  Alert,
  CircularProgress, 
  Tooltip,
   Chip,
   Avatar,
    Stepper,
  Step,
  StepLabel,
  AppBar,
  Toolbar,
  useMediaQuery
  } from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowBack,
  Edit as EditIcon,
  SmartToyOutlined as SmartToyOutlinedIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import { useTheme } from '@mui/material/styles';
import isEqual from 'lodash/isEqual'; 

import SingleRiskReport from './SingleRiskReport';
// Experimental Mantine canvas (feature-flagged)
import AssessmentCanvas from './canvas/AssessmentCanvas.jsx';
import SecurityReportData from './SecurityReportData'; 
import RiskCard from './RiskCard';
import SourceRiskCounter from './SourceRiskCounter';
import RiskCatalogue from './RiskCatalogue';
import Compliance from './Compliance';
import SecurityAssessmentReport from './SecurityAssessmentReport';
import RiskMonitors from './RiskMonitors';
import ActionPlan from './ActionPlan';

import { useAuth0 } from '@auth0/auth0-react';
import { moveRiskToSaved } from './redux/actions/riskActions'
import useSaveOperation from './useSaveOperation';
import { fetchControls } from './redux/actions/controlActions';
// Risk priority colors
const riskPriorityColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB', // fallback
};

const residualRiskColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB', // fallback
};

// For the action plan rating:
const actionPlanColors = {
  Unsatisfactory: '#D32F2F',
  Poor: '#FF5722',
  Fair: '#FFC107',
  Good: '#8BC34A',
  'Very Good': '#66BB6A',
  Excellent: '#4CAF50',
  None: '#EBEBEB', // fallback
};

// Define a blue color palette for the PieChart
const sourceColors = ['#1E88E5', '#43A047', '#FDD835', '#FB8C00', '#8E24AA', '#E53935', '#00897B'];
const bwContained = { backgroundColor: '#000', color: '#fff', '&:hover': { backgroundColor: '#333' } };
const bwOutlined  = { borderColor: '#000',  color: '#000', '&:hover': { backgroundColor: '#F5F5F5', borderColor: '#333' } };

// Utility Functions
const toTitleCase = (str) => {
  if (!str) return '';
  // Handle known acronyms
  str = str.replace(/\b(EU|ROC|AUC)\b/g, match => match.toUpperCase());

  // Format camelCase and PascalCase
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before capitals
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Handle sequences of capitals
    .replace(/\b([Aa]nd)\b/g, '&')
    .split(/\s+/) // Split by spaces
    .map(word => {
      if (['EU', 'ROC', 'AUC'].includes(word)) {
        return word; // Return acronyms unchanged
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(); // Capitalize each word
      }
    })
    .join(' ')
    .trim(); // Trim extra spaces
};

const AssessmentReport = ({ modelId: propModelId, assessmentUuid: propAssessmentUuid}) => {

  // Get modelId from props (when used inline) or URL params (when used as route)
  const { modelId: paramModelId } = useParams();
  const modelId = propModelId || paramModelId;

  const navigate=useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const risksState = useSelector((state) => state.risks.models[modelId] || {});

  // Get assessmentUuid from props (when used inline) or Redux (when used as route)
  const assessmentUuid = propAssessmentUuid || risksState?.assessmentUuid;
  const securityState = useSelector((state) => state.security.models[modelId] || {});
  const allControls = useSelector(selectAllControls);
  const contextState = useSelector((state) => state.context.data[modelId]);
  const contextData = useSelector((state) => state.context.data[modelId] || {});
  //const assessmentUuid = useSelector((state) => state.risks.models[modelId]?.assessmentUuid);
  const residualRiskData = useSelector(
    (state) => state.residualRisk.byModel[modelId] || {}
  );

  const securityReportData = SecurityReportData();
  const riskFormData = useMemo(() => risksState?.riskFormData || {}, [risksState?.riskFormData]);
  const riskCardSections = Object.keys(riskFormData || {});
  const { user } = useAuth0();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get model name from either risks or security state
  const modelName = risksState?.modelName || securityState?.modelName || 'Unknown Model';
  // Local State
 
  const [expandedSection, setExpandedSection] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  // sub‑tab inside the main “Details” tab
  const [detailsSubTabIndex, setDetailsSubTabIndex] = useState(0);
  const [categoryTabIndex, setCategoryTabIndex] = useState({});
  const [expandedRiskCard, setExpandedRiskCard] = useState({});
  const [editingRisk, setEditingRisk] = useState(null);
  const [openPriorityModal, setOpenPriorityModal] = useState(false);
  const [selectedPriorityRisks, setSelectedPriorityRisks] = useState([]);
  const [selectedPriorityName, setSelectedPriorityName] = useState('');
  const [openSourceModal, setOpenSourceModal] = useState(false);
  const [selectedSourceRisks, setSelectedSourceRisks] = useState([]);
  const [selectedSourceName, setSelectedSourceName] = useState('');
  const [selectedSectionName, setSelectedSectionName] = useState('');
  const [openRiskGenerationModal, setOpenRiskGenerationModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['RiskCard & Assessment', 'Risk Catalogue'];
   const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [drawerOpen, setDrawerOpen] = useState({});
// State for Details modal
const [detailsModalOpen, setDetailsModalOpen] = useState(false);
const [selectedSectionDetails, setSelectedSectionDetails] = useState({ summary: [], details: [] });
const [selectedSectionNameForDetails, setSelectedSectionNameForDetails] = useState('');
const [changedCategories, setChangedCategories] = useState([]);
const [stageChange, setStageChange] = useState(null);

const changedCategoriesWithoutRisks = useMemo(() => {
  return changedCategories.filter(cat => !cat.hasRisks);
}, [changedCategories]);



// Helper functions to extract values
const getResidualRiskVal = (riskId) => {
  if (residualRiskData[riskId]) {
    return (
      residualRiskData[riskId].overridden?.residual_rating ||
      residualRiskData[riskId].residual_rating ||
      'N/A'
    );
  }
  return 'N/A';
};

const getActionPlanAnalysisVal = (riskId) => {
  if (residualRiskData[riskId]) {
    return (
      residualRiskData[riskId].overridden?.action_plan_rating ||
      residualRiskData[riskId].action_plan_rating ||
      'N/A'
    );
  }
  return 'N/A';
};

useEffect(() => {
  const changed = [];
  Object.entries(riskFormData).forEach(([section, sectionData]) => {
    const origSources = originalRiskFormDataRef.current[section]?.riskSources || [];
    const currSources = sectionData.riskSources || [];
    const origMap = Object.fromEntries(origSources.map(src => [src.name, src]));
    const currMap = Object.fromEntries(currSources.map(src => [src.name, src]));
    const categories = Array.from(new Set([
      ...Object.keys(origMap),
      ...Object.keys(currMap),
    ]));
    categories.forEach(category => {
      const origEntry = origMap[category] || {};
      const currEntry = currMap[category] || {};
      if (!isEqual(origEntry, currEntry)) {
        const hasRisks = Array.isArray(sectionData.sectionRisks?.[category]) &&
          sectionData.sectionRisks[category].length > 0;
        changed.push({ section, category, hasRisks });
      }
    });
  });
  setChangedCategories(changed);
}, [riskFormData]);
  const { isSaving, isSaved, handleSaveOperation, setIsSaved} = useSaveOperation();

  useEffect(() => {
    if (isSaved) {
      const timer = setTimeout(() => {
        setIsSaved(false);
      }, 2000); // Reset after 2 seconds
      return () => clearTimeout(timer);
    }
  }, [isSaved, setIsSaved]);

  
  // Helper Functions
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Ref to store the original riskFormData
  const originalRiskFormDataRef = useRef(riskFormData);


  // Memoized Data
  const allSectionRisks = useMemo(() => {
    if (!risksState || !risksState.riskFormData) return {};
    const filtered = {};
    Object.entries(risksState.riskFormData).forEach(([section, categories]) => {
      filtered[section] = {};
      Object.entries(categories.sectionRisks || {}).forEach(([category, risks]) => {
        const processedRisks = Array.isArray(risks)
          ? risks.map((risk) => ({
              ...risk,
              status: risk.status || 'none',
            }))
          : [];
        filtered[section][category] = processedRisks;
      });
    });
    return filtered;
  }, [risksState]);

  const allSecurityRisks = useMemo(() => {
    if (!securityReportData.dataByModel || !securityReportData.dataByModel[modelId]?.risks) return [];
    return Array.isArray(securityReportData.dataByModel[modelId].risks)
      ? securityReportData.dataByModel[modelId].risks.map((risk) => ({
          ...risk,
          status: risk.status || 'none',
        }))
      : [];
  }, [securityReportData, modelId]);

  const allSecurityControls = useMemo(() => {
    if (!securityReportData.dataByModel || !securityReportData.dataByModel[modelId]?.controls) return {};
    const controls = {};
    Object.entries(securityReportData.dataByModel[modelId].controls).forEach(([riskId, controlList]) => {
      controls[riskId] = Array.isArray(controlList)
        ? controlList.map((control) => ({
            ...control,
            status: control.status || 'none',
          }))
        : [];
    });
    return controls;
  }, [securityReportData, modelId]);

  const { useCase, businessContext, modelContext } = useMemo(() => {
    if (!contextState) return { useCase: 'none', businessContext: 'none', modelContext: 'none' };
    return {
      useCase: contextState.useCase?.narrative || 'none',
      businessContext: contextState.businessContext || 'none',
      modelContext: contextState.modelContext || 'none',
    };
  }, [contextState]);

  const narratives = useMemo(() => {
    return Object.keys(contextData).reduce((acc, section) => {
      const narrative = contextData[section]?.narrative
        ? contextData[section].narrative
        : `No narrative for ${section}`;
      acc[section] = narrative;
      return acc;
    }, {});
  }, [contextData]);

  const riskCounts = useMemo(() => {
    const counts = {
      Critical: 0,
      High: 0,
      Significant: 0,
      Moderate: 0,
      Low: 0,
      Negligible: 0,
      None: 0,
    };

    // Process Model Risks
    Object.values(allSectionRisks).forEach((categories) => {
      Object.values(categories).forEach((risks) => {
        risks.forEach((risk) => {
          const priority = capitalizeFirstLetter(risk.priority || 'none');
          if (counts.hasOwnProperty(priority)) {
            counts[priority] += 1;
          } else {
            // Handle unexpected priority by assigning to 'None'
            counts['None'] += 1;
            console.warn(`Unexpected priority "${priority}" encountered. Assigned to 'None'.`);
          }
        });
      });
    });

    // Process Security Risks
    allSecurityRisks.forEach((risk) => {
      const priority = capitalizeFirstLetter(risk.threat_level || 'none');
      if (counts.hasOwnProperty(priority)) {
        counts[priority] += 1;
      } else {
        // Handle unexpected priority by assigning to 'None'
        counts['None'] += 1;
        console.warn(`Unexpected threat level "${priority}" encountered. Assigned to 'None'.`);
      }
    });

    return counts;
  }, [allSectionRisks, allSecurityRisks]);

  // Prepare riskPriorityData with risk names
  const riskPriorityData = useMemo(() => {
    return Object.keys(riskCounts).map((key) => ({
      name: key,
      count: riskCounts[key],
      risks: [], // Initialize an array to store risk names
    }));
  }, [riskCounts]);

  // Populate the risks for each priority
  useMemo(() => {
    // Clear existing risks
    riskPriorityData.forEach((data) => {
      data.risks = [];
    });

    // Add Model Risks
    Object.values(allSectionRisks).forEach((categories) => {
      Object.values(categories).forEach((risks) => {
        risks.forEach((risk) => {
          const priority = capitalizeFirstLetter(risk.priority || 'none');
          const riskName = risk.riskName || 'Unnamed Risk';
          const priorityData = riskPriorityData.find((data) => data.name === priority);
          if (priorityData) {
            priorityData.risks.push(riskName);
          } else {
            // Assign to 'None' if priority not found
            const noneData = riskPriorityData.find((data) => data.name === 'None');
            if (noneData) {
              noneData.risks.push(riskName);
            }
          }
        });
      });
    });

    // Add Security Risks
    allSecurityRisks.forEach((risk) => {
      const priority = capitalizeFirstLetter(risk.threat_level || 'none');
      const riskName = risk.risk_name || 'Unnamed Security Risk';
      const priorityData = riskPriorityData.find((data) => data.name === priority);
      if (priorityData) {
        priorityData.risks.push(riskName);
      } else {
        // Assign to 'None' if priority not found
        const noneData = riskPriorityData.find((data) => data.name === 'None');
        if (noneData) {
          noneData.risks.push(riskName);
        }
      }
    });
  }, [allSectionRisks, allSecurityRisks, riskPriorityData]);

  const riskPriorityChartData = useMemo(() => {
    const counts = Object.keys(riskPriorityColors).reduce((acc, key) => {
      acc[key] = { security: 0, model: 0 };
      return acc;
    }, {});

    // Calculate counts for model risks
    Object.values(allSectionRisks).forEach((categories) => {
      Object.values(categories).forEach((risks) => {
        risks.forEach((risk) => {
          const priority = capitalizeFirstLetter(risk.priority || 'None');
          if (counts.hasOwnProperty(priority)) {
            counts[priority].model += 1;
          } else {
            // Assign to 'None' if unexpected priority
            counts['None'].model += 1;
            console.warn(`Unexpected priority "${priority}" in model risks. Assigned to 'None'.`);
          }
        });
      });
    });

    // Calculate counts for security risks
    allSecurityRisks.forEach((risk) => {
      const priority = capitalizeFirstLetter(risk.threat_level || 'None');
      if (counts.hasOwnProperty(priority)) {
        counts[priority].security += 1;
      } else {
        // Assign to 'None' if unexpected priority
        counts['None'].security += 1;
        console.warn(`Unexpected threat level "${priority}" in security risks. Assigned to 'None'.`);
      }
    });

    return Object.keys(counts).map((priority) => ({
      name: priority,
      ...counts[priority],
    }));
  }, [allSectionRisks, allSecurityRisks]);

  // Prepare sectionDistributionData for PieChart
  const sectionDistributionData = useMemo(() => {
    const sourceRiskCounts = {};

    // Collect risks from all sections
    Object.entries(allSectionRisks).forEach(([section, categories]) => {
      let totalRisks = 0;
      let riskNames = [];
      Object.values(categories).forEach((risks) => {
        if (Array.isArray(risks)) {
          totalRisks += risks.length;
          riskNames = riskNames.concat(risks.map((risk) => risk.riskName || risk.name || 'Unnamed Risk'));
        }
      });
      sourceRiskCounts[toTitleCase(section)] = {
        count: totalRisks,
        risks: riskNames,
      };
    });

    // Include security risks
    sourceRiskCounts['Security'] = {
      count: allSecurityRisks.length,
      risks: allSecurityRisks.map((risk) => risk.risk_name || 'Unnamed Security Risk'),
    };

    // Convert to array for pie chart
    return Object.keys(sourceRiskCounts).map((key) => ({
      name: key,
      count: sourceRiskCounts[key].count,
      risks: Array.isArray(sourceRiskCounts[key].risks) ? sourceRiskCounts[key].risks : [],
    }));
  }, [allSectionRisks, allSecurityRisks]);


  

  // Custom Tooltip Component for BarChart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const risks = payload[0].payload.risks;
      return (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">{`${toTitleCase(label)} Risks`}</Typography>
          <ul>
            {Array.isArray(risks) && risks.length > 0 ? (
              risks.map((riskName, index) => (
                <li key={index}>{riskName}</li>
              ))
            ) : (
              <li>No risks available.</li>
            )}
          </ul>
        </Paper>
      );
    }
    return null;
  };

  // Custom Tooltip Component for PieChart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">{`${toTitleCase(data.name)} Risks`}</Typography>
          <ul>
            {Array.isArray(data.risks) && data.risks.length > 0 ? (
              data.risks.map((riskName, index) => (
                <li key={index}>{riskName}</li>
              ))
            ) : (
              <li>No risks available.</li>
            )}
          </ul>
        </Paper>
      );
    }
    return null;
  };
  const legendFormatter = (value, entry) => {
    //const { color } = entry;
    
    return (
      <span style={{ color: '#333' }}>
        {value} ({entry.payload.count})
      </span>
      
    );
  };
  // Event Handlers
  const handleBarClick = (data) => {
    if (data && data.risks) {
      setSelectedPriorityRisks(data.risks);
      setSelectedPriorityName(data.name);
      setOpenPriorityModal(true);
    }
  };

  const handleClosePriorityModal = () => {
    setOpenPriorityModal(false);
  };

  const handlePieClick = (data) => {
    if (data && data.risks) {
      setSelectedSourceName(data.name);
      setSelectedSourceRisks(data.risks);
      setOpenSourceModal(true);
    }
  };

  const handleCloseSourceModal = () => {
    setOpenSourceModal(false);
  };

  const handleExpandSection = (sectionName) => {
    setExpandedSection((prevExpandedSection) => {
      const isExpanding = !prevExpandedSection[sectionName];
      if (isExpanding && categoryTabIndex[sectionName] === undefined) {
        setCategoryTabIndex((prevCategoryTabIndex) => ({
          ...prevCategoryTabIndex,
          [sectionName]: 0,
        }));
      }
      return {
        ...prevExpandedSection,
        [sectionName]: isExpanding,
      };
    });
  };

  const handleEditRisk = (riskData, sectionName, category) => {
    setEditingRisk({ risk: riskData, sectionName, category });
    console.log('Editing risk:', riskData);
  };

  const handleCloseEditRisk = () => {
    setEditingRisk(null);
  };




  const handleSaveRisk = useCallback(
    async (risk, sectionName, category) => {
      if (!risk.riskId) {
        handleSaveOperation(async () => {
          const ownerInfo = {
            name: user.name,
            email: user.email,
            picture: user.picture,
          };
          const response = await fetch('/.netlify/functions/saveRisk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: risk.riskName,
              section: sectionName,
              category: category,
              modelUuid: modelId,
              assessmentUuid: assessmentUuid,
              owner:ownerInfo,
            }),
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to save risk');
          }
          const savedRisk = await response.json();
          dispatch(
            moveRiskToSaved(modelId, sectionName, {
              ...risk,
              riskId: savedRisk.riskId,
              status: 'Saved',
              category: category,
              owner: {
                name: user.name,
                email: user.email,
                picture: user.picture,
              },
            })
          );
          setSnackbarMessage('Risk saved successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }).catch((error) => {
          setSnackbarMessage(error.message);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
      }
    },
    [handleSaveOperation, dispatch, user.name, user.email, user.picture,modelId, assessmentUuid]
  );

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setExpandedSection({});
  };

  const handleCategoryTabChange = (sectionName, newIndex) => {
    setCategoryTabIndex((prev) => ({
      ...prev,
      [sectionName]: newIndex,
    }));
  };


  // Render controls count for each risk
  const renderControlsCount = (riskId, isSecurityRisk = false) => {
    const controls = isSecurityRisk ? allSecurityControls[riskId] : allControls[riskId];
    console.log(`renderControlsCount called with riskId: '${riskId}'`);
    console.log(`controls retrieved:`, controls);
    return Array.isArray(controls) ? controls.length : 0;
  };

  const handleUpdateControls = (sectionName) => {
      const riskAreaData = riskFormData[sectionName]?.riskSources || [];
      const risks         = allSectionRisks[sectionName]?.flat() || [];
      dispatch(fetchControls({
        sectionName,
        narratives,
        riskSourceData: riskAreaData,
        sectionRisks: risks,
        controlModelType: risksState.modelType,
        controlModelStage: stageChange?.to,
        //controlPreviousStage: stageChange?.from
      }));
      setStageChange(null);
    };

  const handleExpandRiskCard = (sectionName) => {
    setExpandedRiskCard((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const handleAddRiskManually = (sectionName) => {
    console.log(`Adding a risk manually for section: ${sectionName}`);
    // Implement your logic here, e.g., open a modal to input new risk details
  };

  // Handlers for Risk Generation Modal
  const handleOpenRiskGenerationModal = (sectionName) => {
    setSelectedSectionName(sectionName);
    setOpenRiskGenerationModal(true);
    setActiveStep(0);
  };

  const handleCloseRiskGenerationModal = () => {
    setOpenRiskGenerationModal(false);
    setSelectedSectionName('');
    setActiveStep(0);
  };


  // Modal Styling
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  // Early Exit: If no risks and no security risks, display a message
  const hasNoRisks = useMemo(() => {
    const totalModelRisks = Object.values(allSectionRisks).reduce((acc, categories) => {
      return acc + Object.values(categories).reduce((catAcc, risks) => catAcc + (Array.isArray(risks) ? risks.length : 0), 0);
    }, 0);
    const totalSecurityRisks = Array.isArray(allSecurityRisks) ? allSecurityRisks.length : 0;
    return totalModelRisks === 0 && totalSecurityRisks === 0;
  }, [allSectionRisks, allSecurityRisks]);


const formatValue = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'None';
    }
    return (
      <ul>
        {value.map((item, index) => (
          <li key={index}>{formatValue(item)}</li>
        ))}
      </ul>
    );
  } else if (typeof value === 'object' && value !== null) {
    if (Object.keys(value).length === 0) {
      return 'None';
    }
    return (
      <ul>
        {Object.entries(value).map(([key, val], index) => (
          <li key={index}>
            <strong>{toTitleCase(key)}:</strong> {formatValue(val)}
          </li>
        ))}
      </ul>
    );
  } else {
    return value ? value.toString() : 'None';
  }
};

 const findDifferences = (original, current, path = []) => {
  let differences = [];

  // Get all unique keys from both original and current
  const keys = new Set([...Object.keys(original || {}), ...Object.keys(current || {})]);

  keys.forEach((key) => {
    // Exclude certain keys
    if (key === 'sectionRisks' || key === 'assessmentProgress') return;

    const origValue = original ? original[key] : undefined;
    const currValue = current ? current[key] : undefined;
    let currentPath = [...path];

    // Use 'name' instead of key if available
    if (typeof currValue === 'object' && currValue !== null && currValue.hasOwnProperty('name')) {
      currentPath.push(currValue.name);
    } else if (typeof origValue === 'object' && origValue !== null && origValue.hasOwnProperty('name')) {
      currentPath.push(origValue.name);
    } else {
      currentPath.push(toTitleCase(key));
    }

    if (typeof origValue === 'object' && origValue !== null && typeof currValue === 'object' && currValue !== null) {
      // Recurse into nested objects
      differences = differences.concat(findDifferences(origValue, currValue, currentPath));
    } else if (!isEqual(origValue, currValue)) {
      // Record the difference
      differences.push({
        path: currentPath.join(' > '),
        original: origValue,
        new: currValue,
      });
    }
  });

  return differences;
}; 


  // Replace your existing getChangedFields with this:
const getChangedFields = (sectionName) => {
  // Grab only the riskSources arrays
  const origSources = originalRiskFormDataRef.current[sectionName]?.riskSources || [];
  const currSources = riskFormData[sectionName]?.riskSources || [];

  // Build maps keyed by the source “name”
  const origMap = Object.fromEntries(origSources.map(src => [src.name, src]));
  const currMap = Object.fromEntries(currSources.map(src => [src.name, src]));

  // Compute diffs between those two maps
  const differences = findDifferences(origMap, currMap);

  if (differences.length === 0) {
    return { summary: 'No changes detected.', details: null };
  }

  // Pull out just the top-level names that changed
  const changedCats = Array.from(
    new Set(differences.map(diff => diff.path.split(' > ')[0]))
  );

  return { summary: changedCats, details: differences };
};

  const handleOpenDetailsModal = (sectionName) => {
    const { summary, details } = getChangedFields(sectionName);
    setSelectedSectionDetails({ summary, details });
    setSelectedSectionNameForDetails(sectionName);
    setDetailsModalOpen(true);
  };
  
  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedSectionDetails({ summary: [], details: [] });
    setSelectedSectionNameForDetails('');
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };


  // Default to new Mantine-based Assessment Canvas
  const urlParams = new URLSearchParams(window.location.search);
  // Use legacy view only if explicitly requested with ?legacy=1
  const useLegacy = urlParams.get('legacy') === '1';

  if (!useLegacy) {
    // Default to canvas view for better user experience
    return (
      <AssessmentCanvas modelId={modelId} assessmentUuid={assessmentUuid} />
    );
  }

  return (

    <Box sx={{ backgroundColor: '#FAFAFA', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
  
    <AppBar position="static" elevation={0} sx={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E0E0E0' }}>
      <Toolbar>
        <IconButton edge="start" onClick={() => navigate(-1)} aria-label="back" sx={{ mr: 2 }}>
          <ArrowBack sx={{ color: '#42424a' }} />
        </IconButton>
        <Typography variant="h6" color="text.primary" sx={{ flexGrow: 1 }}>
          Assessment Report - {modelName}
        </Typography>
      </Toolbar>
    </AppBar>
    <Box sx={{ flexGrow: 1, padding: 2 }}>
      <Grid container spacing={3}>
        {editingRisk ? (
          // Render SingleRiskReport when editingRisk is set
          <Grid item xs={12}>
            <SingleRiskReport
              risk={editingRisk.risk}
              sectionName={editingRisk.sectionName}
              sectionState={riskFormData[editingRisk.sectionName]}
              category={editingRisk.category}
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              user={user}
              onClose={handleCloseEditRisk} // Handle closing the edit view
            />
          </Grid>
        ) : hasNoRisks ? (
          // Display a message when there are no risks
          <Grid item xs={12}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No Risks Available
              </Typography>
              <Typography variant="body1" gutterBottom>
                There are currently no risks to display. You can add risks manually or generate them.
              </Typography>
              <Box display="flex" justifyContent="center" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<SmartToyOutlinedIcon />}
                  sx={bwOutlined}
                  onClick={() => handleOpenRiskGenerationModal('general')}
                >
                  Generate Risks
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddRiskManually('general')}
                >
                  Add Risk Manually
                </Button>
              </Box>
            </Paper>
          </Grid>
        ) : (
          <>
            {/* Tabs for Different Sections */}
            <Grid item xs={12}>
              <Paper elevation={3}>
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  centered
                  variant="fullWidth"
                >
                  <Tab label="Safety & Security Risks" />
                  <Tab label="Action Plan" />
                  <Tab label="Compliance" />
                  <Tab label="Monitors & Analyzers" />
                  <Tab label="Details" />
                 </Tabs>
              </Paper>
            </Grid>

            {/* Tab Content */}
            {tabIndex === 0 && (
              <>
                {/* Risks by Priority - BarChart */}
                <Grid item xs={12} sm={6} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Risks by Priority
                    </Typography>
                    {riskPriorityChartData && riskPriorityChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={riskPriorityChartData}
                          onClick={(data) => {
                            if (data.activePayload && data.activePayload.length > 0) {
                              handleBarClick(data.activePayload[0].payload);
                            }
                          }}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={60} />
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip content={<CustomBarTooltip />} />

                          {/* Bar for Model Risks */}
                          <Bar dataKey="model" stackId="a">
                            {riskPriorityChartData.map((entry, index) => (
                              <Cell key={`cell-model-${index}`} fill={riskPriorityColors[entry.name] || '#CCCCCC'} />
                            ))}
                          </Bar>

                          {/* Bar for Security Risks */}
                          <Bar dataKey="security" stackId="a">
                            {riskPriorityChartData.map((entry, index) => (
                              <Cell
                                key={`cell-security-${index}`}
                                fill={riskPriorityColors[entry.name] || '#CCCCCC'}
                                style={{ stroke: '#FFFFFF', strokeWidth: 2 }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body1">No data available for Risks by Priority.</Typography>
                    )}
                  </Paper>
                </Grid>




<Grid item xs={12} sm={6} md={6}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom align="center">
          Risks by Source
        </Typography>
        {sectionDistributionData && sectionDistributionData.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sectionDistributionData}
                  dataKey="count"
                  nameKey="name"
                  cx={isSmallScreen ? '50%' : '30%'}
                  cy="50%"
                  outerRadius={80}
                  labelLine={false}
                  label={false}
                  onClick={(data, index) => handlePieClick(data.payload)}
                >
                  {sectionDistributionData.map((entry, index) => (
                    <Cell
                      key={`cell-pie-${index}`}
                      fill={sourceColors[index % sourceColors.length]}

                    />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomPieTooltip />} />
                {/* <Legend
                  layout={isSmallScreen ? 'horizontal' : 'vertical'}
                  align={isSmallScreen ? 'center' : 'right'}
                  verticalAlign={isSmallScreen ? 'bottom' : 'middle'}
                  iconType="circle"
                  formatter={legendFormatter}
                  wrapperStyle={{
              +     fontSize: 12,
              +     paddingLeft: isSmallScreen ? 0 : 20,
              +     paddingTop: isSmallScreen ? 20 : 0,
              +   }}
                /> */}
                <Legend
                 layout={'vertical'}
                 align={'center'}
                 verticalAlign={ 'bottom'} 
                 iconType="circle"
                 formatter={legendFormatter} 
                 wrapperStyle={{
                    fontSize: 12,
                    paddingLeft: isSmallScreen ? 0 : 20,
                    paddingTop:  isSmallScreen ? 20 : 0,
                  }}
/>
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body1">
            No data available for Risks by Source.
          </Typography>
        )}
      </Paper>
    </Grid>

                {/* Risks & Controls Table */}
                <Grid item xs={12}>
  <Paper sx={{ p: 3 }}>
    <Typography variant="h6" gutterBottom>
      Risks & Mitigations
    </Typography>
    <Divider sx={{ mb: 2 }} />

    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Risk Source</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Risks By Priority</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Residual Risk</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {Object.entries(riskFormData).map(([sectionName, sectionData]) => {
            /* ---------- helpers identical to the previous (working) version ---------- */
            const hasNoRisksInSection = !Object.values(sectionData.sectionRisks || {})
              .some(risks => Array.isArray(risks) && risks.length > 0);

            const isChanged = changedCategories.some(c => c.section === sectionName);

            const changedCategoriesForSection = changedCategoriesWithoutRisks
              .filter(c => c.section === sectionName);

            const existingCategories = Object.keys(sectionData.sectionRisks || {});
            const combinedCategories = [
              ...existingCategories,
              ...changedCategoriesForSection.map(c => c.category),
            ];
            const ordered = [
              ...combinedCategories.filter(cat => cat !== 'Other'),
              ...combinedCategories.filter(cat => cat === 'Other'),
            ];

            const categoryInfo = ordered.map(category => {
              const hasRisks = Array.isArray(sectionData.sectionRisks?.[category]) &&
                               sectionData.sectionRisks[category].length > 0;
              const isNew    = changedCategoriesForSection
                                .some(c => c.category === category) && !hasRisks;
              return { name: category, hasRisks, isNew };
            });
            /* ------------------------------------------------------------------------ */

            return (
              <React.Fragment key={sectionName}>
                {/* ───────────── source row ───────────── */}
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <IconButton
                        onClick={() => handleExpandSection(sectionName)}
                        size="small"
                      >
                        {expandedSection[sectionName] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <Typography sx={{ ml: 1 }}>{toTitleCase(sectionName)}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <SourceRiskCounter sectionRisks={sectionData.sectionRisks} />
                  </TableCell>

                  <TableCell>
                    {(() => {
                      const hasRR = Object.values(sectionData.sectionRisks || {})
                        .flat()
                        .filter(r => residualRiskData[r.riskId]);
                      return hasRR.length || '—';
                    })()}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={isChanged ? 'Changed' : 'Unchanged'}
                      color={isChanged ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>

                  {/* Details chip / “see more” */}
                  <TableCell>
                    {isChanged && (() => {
                      const { summary } = getChangedFields(sectionName);
                      return summary === 'No changes detected.' ? (
                        <Typography>{summary}</Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2">
                            Changes detected in: {summary.join(', ')}
                          </Typography>
                          <Button
                            variant="text"
                            sx={{ textTransform: 'none' }}
                            onClick={() => handleOpenDetailsModal(sectionName)}
                          >
                            See more
                          </Button>
                        </Box>
                      );
                    })()}
                  </TableCell>

                  {/* Actions column – always allow risk generation when none exist */}
                  <TableCell>
                    {hasNoRisksInSection ? (
                      <>
                        <Tooltip title="Add risk manually">
                          <IconButton
                            color="secondary"
                            onClick={() => handleAddRiskManually(sectionName)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate risks">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenRiskGenerationModal(sectionName)}
                          >
                            <SmartToyOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      isChanged && (
                        <Tooltip title="Generate risks">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenRiskGenerationModal(sectionName)}
                          >
                            <SmartToyOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    )}

                    {stageChange && (
                      <Tooltip title="Update controls to new stage">
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateControls(sectionName)}
                        >
                          <SmartToyOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>

                {/* ───────────── collapsible detail row ───────────── */}
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedSection[sectionName]} timeout="auto" unmountOnExit>
                      <Grid container spacing={1} sx={{ p: 1 }}>
                        {hasNoRisksInSection ? (
                          /* ======= section completely empty – show message & buttons ======= */
                          <Grid item xs={12}>
                            <Typography>No risks for this risk source.</Typography>
                            <Box display="flex" gap={2} mt={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SmartToyOutlinedIcon />}
                                onClick={() => handleOpenRiskGenerationModal(sectionName)}
                                sx={bwOutlined}
                              >
                                Generate Risks
                              </Button>
                              <IconButton
                                color="secondary"
                                onClick={() => handleAddRiskManually(sectionName)}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        ) : (
                          <>
                            {/* left‑side vertical tabs */}
                            <Grid item xs={3}>
                              <Tabs
                                orientation="vertical"
                                variant="scrollable"
                                value={categoryTabIndex[sectionName] || 0}
                                onChange={(e, idx) => handleCategoryTabChange(sectionName, idx)}
                                sx={{ alignItems: 'flex-start', pr: 1 }}
                              >
                                {categoryInfo.map(cat => (
                                  <Tab
                                    key={cat.name}
                                    label={
                                      <Box display="flex" alignItems="center">
                                        {toTitleCase(cat.name)}
                                        {cat.isNew && (
                                          <Chip
                                            label="New"
                                            size="small"
                                            color="success"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </Box>
                                    }
                                    sx={{ textTransform: 'none' }}
                                  />
                                ))}
                              </Tabs>
                            </Grid>

                            {categoryInfo.map((cat, idx) => (
                              <Grid
                                item
                                xs={9}
                                key={cat.name}
                                hidden={(categoryTabIndex[sectionName] || 0) !== idx}
                              >
                                <Box display="flex" justifyContent="flex-end" mb={1}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      setDrawerOpen(prev => ({ ...prev, [cat.name]: true }))
                                    }
                                  >
                                    View/Edit RiskCard
                                  </Button>
                                </Box>

                                {cat.hasRisks ? (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Owner</TableCell>
                                        <TableCell>Risk Name</TableCell>
                                        <TableCell>Priority</TableCell>
                                        <TableCell>Residual Risk</TableCell>
                                        <TableCell>Action Plan</TableCell>
                                        <TableCell>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {sectionData.sectionRisks[cat.name].map(risk => (
                                        <TableRow key={risk.riskId}>
                                          <TableCell>
                                            {risk.owner ? (
                                              <Tooltip title={risk.owner.name}>
                                                <Avatar
                                                  alt={risk.owner.name}
                                                  src={risk.owner.picture}
                                                />
                                              </Tooltip>
                                            ) : (
                                              'No owner'
                                            )}
                                          </TableCell>
                                          <TableCell>{risk.riskName}</TableCell>
                                          <TableCell>
                                            {(() => {
                                              const prioText = capitalizeFirstLetter(risk.priority || 'none');
                                              const prioColor =
                                                riskPriorityColors[prioText] || riskPriorityColors.None;
                                              return (
                                                <Chip
                                                  label={prioText}
                                                  sx={{ backgroundColor: prioColor, color: '#fff' }}
                                                />
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                              const rrVal   = getResidualRiskVal(risk.riskId);
                                              const rrColor = residualRiskColors[rrVal] || residualRiskColors.None;
                                              return (
                                                <Chip
                                                  label={rrVal}
                                                  sx={{ backgroundColor: rrColor, color: '#fff' }}
                                                />
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                              const apVal   = getActionPlanAnalysisVal(risk.riskId);
                                              const apColor = actionPlanColors[apVal] || actionPlanColors.None;
                                              return (
                                                <Chip
                                                  label={apVal}
                                                  sx={{ backgroundColor: apColor, color: '#fff' }}
                                                />
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>
                                            <IconButton
                                              onClick={() => handleEditRisk(risk, sectionName, cat.name)}
                                            >
                                              <EditIcon />
                                            </IconButton>
                                            {risk.status !== 'Saved' && (
                                              <IconButton
                                                onClick={() =>
                                                  handleSaveRisk(risk, sectionName, cat.name)
                                                }
                                                disabled={isSaving}
                                              >
                                                {isSaving ? (
                                                  <CircularProgress size={24} />
                                                ) : (
                                                  <SaveIcon />
                                                )}
                                              </IconButton>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Box>
                                    <Typography>No risks generated</Typography>
                                    <IconButton
                                      onClick={() =>
                                        handleOpenRiskGenerationModal(sectionName, cat.name)
                                      }
                                    >
                                      <SmartToyOutlinedIcon />
                                    </IconButton>
                                  </Box>
                                )}

                                {/* right‑side drawer with RiskCard */}
                                <Drawer
                                  anchor="right"
                                  open={!!drawerOpen[cat.name]}
                                  onClose={() =>
                                    setDrawerOpen(prev => ({ ...prev, [cat.name]: false }))
                                  }
                                >
                                  <Box sx={{ width: 600, p: 2 }}>
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      mb={2}
                                    >
                                      <Typography variant="h6">
                                        {toTitleCase(cat.name)} Risk Card
                                      </Typography>
                                      <IconButton
                                        onClick={() =>
                                          setDrawerOpen(prev => ({ ...prev, [cat.name]: false }))
                                        }
                                      >
                                        <CloseIcon />
                                      </IconButton>
                                    </Box>
                                    <RiskCard
                                      modelId={modelId}
                                      sectionName={sectionName}
                                      categoryName={cat.name}
                                      assessmentUuid={assessmentUuid}
                                    />
                                  </Box>
                                </Drawer>
                              </Grid>
                            ))}
                          </>
                        )}
                      </Grid>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}

          {/* Security Risks ‑–– left untouched */}
          <SecurityAssessmentReport
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            expandedSection={expandedSection}
            handleExpandSection={handleExpandSection}
            renderControlsCount={renderControlsCount}
          />
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
</Grid>
              </>
            )}
            {tabIndex === 1 && (
                <Grid item xs={12}>
                  <ActionPlan
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  riskFormData={riskFormData} />
                </Grid>
              )}

              {tabIndex ===  2 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Compliance
                      modelId={modelId}
                      assessmentUuid={assessmentUuid}
                      riskFormData={riskFormData}
                      narratives={narratives}
                    />
                  </Paper>
                </Grid>
              )}
            {tabIndex === 3 && (
              <Grid item xs={12}>
                <RiskMonitors
                  modelId={modelId}
                />
              </Grid>
            )}

            {/* Details Tab with sub-tabs */}
            {tabIndex === 4 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  {/* ─── sub‑tabs inside Details ─── */}
                  <Tabs
                    value={detailsSubTabIndex}
                    onChange={(e, v) => setDetailsSubTabIndex(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    centered
                    variant="fullWidth"
                    sx={{ mb: 2 }}
                  >
                    <Tab label="Risk Card" />
                    <Tab label="Context" />
                  </Tabs>

                  {/* ─── Risk Card (sub‑tab 0) ─── */}
                  {detailsSubTabIndex === 0 && (
                    <>
                      {riskCardSections.length > 0 ? (
                        riskCardSections.map((sectionName) => (
                          <Accordion
                            key={sectionName}
                            expanded={expandedRiskCard[sectionName] || false}
                            onChange={() => handleExpandRiskCard(sectionName)}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              aria-controls={`${sectionName}-content`}
                              id={`${sectionName}-header`}
                            >
                              <Typography>{toTitleCase(sectionName)}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <RiskCard
                                modelId={modelId}
                                sectionName={sectionName}
                                assessmentUuid={assessmentUuid}
                                sx={{ width: '100%' }}
                              />
                            </AccordionDetails>
                          </Accordion>
                        ))
                      ) : (
                        <Typography variant="body1">No Risk Card sections available.</Typography>
                      )}
                    </>
                  )}

                  {/* ─── Context (sub‑tab 1) ─── */}
                  {detailsSubTabIndex === 1 && (
                    <Grid container spacing={2}>
                      {/* vertical tabs on the left */}
                      <Grid item xs={3}>
                        <Tabs
                          orientation="vertical"
                          value={categoryTabIndex['context'] || 0}
                          onChange={(e, newIndex) =>
                            handleCategoryTabChange('context', newIndex)
                          }
                          sx={{ borderRight: 1, borderColor: 'divider' }}
                        >
                          <Tab label="Use Case" />
                          <Tab label="Business Context" />
                          <Tab label="Model Context" />
                        </Tabs>
                      </Grid>

                      {/* content on the right */}
                      <Grid item xs={9}>
                        <Box>
                          {/* Use Case */}
                          <Box hidden={categoryTabIndex['context'] !== 0}>
                            <Typography variant="subtitle1">
                              <strong>Use Case:</strong>
                            </Typography>
                            {typeof useCase === 'object' && useCase !== null ? (
                              Object.entries(useCase).map(([key, value]) => (
                                <Typography key={key} variant="body1" sx={{ mb: 1 }}>
                                  <strong>{key}:</strong> {value}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body1" sx={{ mb: 2 }}>
                                {useCase}
                              </Typography>
                            )}
                          </Box>

                          {/* Business Context */}
                          <Box hidden={categoryTabIndex['context'] !== 1}>
                            <Typography variant="subtitle1">
                              <strong>Business Context:</strong>
                            </Typography>
                            {typeof businessContext === 'object' && businessContext !== null ? (
                              Object.entries(businessContext).map(([key, value]) => (
                                <Typography key={key} variant="body1" sx={{ mb: 1 }}>
                                  <strong>{key}:</strong> {value}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body1" sx={{ mb: 2 }}>
                                {businessContext}
                              </Typography>
                            )}
                          </Box>

                          {/* Model Context */}
                          <Box hidden={categoryTabIndex['context'] !== 2}>
                            <Typography variant="subtitle1">
                              <strong>Model Context:</strong>
                            </Typography>
                            {typeof modelContext === 'object' && modelContext !== null ? (
                              Object.entries(modelContext).map(([key, value]) => (
                                <Typography key={key} variant="body1" sx={{ mb: 1 }}>
                                  <strong>{key}:</strong> {value}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body1">{modelContext}</Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              </Grid>
            )}
          </>
        )}

        {/* Priority Modal */}
        <Modal
          open={openPriorityModal}
          onClose={handleClosePriorityModal}
          aria-labelledby="priority-modal-title"
          aria-describedby="priority-modal-description"
        >
          <Box sx={modalStyle}>
            <Typography id="priority-modal-title" variant="h6">
              Risks with {selectedPriorityName} Priority
            </Typography>
            {Array.isArray(selectedPriorityRisks) && selectedPriorityRisks.length > 0 ? (
              <ul>
                {selectedPriorityRisks.map((riskName, index) => (
                  <li key={index}>{riskName}</li>
                ))}
              </ul>
            ) : (
              <Typography variant="body1">No risks available for this priority.</Typography>
            )}
          </Box>
        </Modal>

        {/* Source Modal */}
        <Modal
          open={openSourceModal}
          onClose={handleCloseSourceModal}
          aria-labelledby="source-modal-title"
          aria-describedby="source-modal-description"
        >
          <Box sx={modalStyle}>
            <Typography id="source-modal-title" variant="h6">
              Risks from {selectedSourceName}
            </Typography>
            {Array.isArray(selectedSourceRisks) && selectedSourceRisks.length > 0 ? (
              <ul>
                {selectedSourceRisks.map((riskName, index) => (
                  <li key={index}>{riskName}</li>
                ))}
              </ul>
            ) : (
              <Typography variant="body1">No risks available from this source.</Typography>
            )}
          </Box>
        </Modal>

        {/* Risk Generation Modal */}
        <Modal
          open={openRiskGenerationModal}
          onClose={handleCloseRiskGenerationModal}
          aria-labelledby="risk-generation-modal-title"
          aria-describedby="risk-generation-modal-description"
          disableEnforceFocus
          keepMounted
        >
          <Box sx={modalStyle}>
            <Typography id="risk-generation-modal-title" variant="h6" component="h2" gutterBottom>
              Generate Risks
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* RiskCard Component for Context Input */}
            {activeStep === 0 && (
                <Box>
            <RiskCard
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              sectionName={selectedSectionName}
            />
              </Box>
          )}

            <Divider sx={{ my: 2 }} />

            {/* RiskCatalogue Component to Display Generated Risks */}
            {activeStep === 1 && (
               <Box>
            <RiskCatalogue
              modelId={modelId}
              sectionName={selectedSectionName}
              assessmentUuid={assessmentUuid}
              narratives={narratives}
            />
              </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  sx={bwOutlined}
                >
                  Back
                </Button>
                <Box>
                  {activeStep < steps.length - 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      sx={{
                        bwContained
                        , mr: 1 }}
                    >
                      Next
                    </Button>
                  )}
                  {activeStep === steps.length - 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCloseRiskGenerationModal}
                      sx={bwContained}
                    >
                      Finish
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseRiskGenerationModal}
                    sx={{ ml: 1, bwOutlined  }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Box>
          </Modal>



{/* Details Modal */}
<Modal
  open={detailsModalOpen}
  onClose={handleCloseDetailsModal}
  aria-labelledby="details-modal-title"
  aria-describedby="details-modal-description"
>
  <Box sx={modalStyle}>
    <Typography id="details-modal-title" variant="h6" component="h2" gutterBottom>
      Detailed Changes for {toTitleCase(selectedSectionNameForDetails)}
    </Typography>
    <Box sx={{ mt: 2 }}>
      {selectedSectionDetails.details && selectedSectionDetails.details.length > 0 ? (
        selectedSectionDetails.details.map((diff, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
              {diff.path}:
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" component="div">
                <strong>Original:</strong> {formatValue(diff.original)}
              </Typography>
              <Typography variant="body2" component="div" color="primary">
                <strong>New:</strong> {formatValue(diff.new)}
              </Typography>
            </Box>
          </Box>
        ))
      ) : (
        <Typography>No detailed changes available.</Typography>
      )}
    </Box>
    <Button variant="contained" onClick={handleCloseDetailsModal} sx={{ mt: 2 , bwContained}}>
      Close
    </Button>
  </Box>
</Modal>
      </Grid>
      <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={handleSnackbarClose}
    >
      <Alert
        onClose={handleSnackbarClose}
        severity={snackbarSeverity}
        sx={{ width: '100%' }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </Box>
    </Box>
  );
};

export default AssessmentReport;
